/**
 * Contextual help: one markdown file per screen key, rendered in the `?` side panel with react-markdown.
 * Each file has frontmatter (title, key, docsUrl) — strip it with `parseHelpArticle` before rendering.
 * Links inside articles use `help:<key>` (open another help article in the panel) and `docs:<path>` (docs site page);
 * pass `helpUrlTransform` to react-markdown's `urlTransform` so they survive sanitising.
 */
export const HELP_KEYS = [
  'home', 'onboarding', 'checkin.daily', 'review.weekly',
  'accounts', 'accounts.new',
  'transactions', 'transactions.new', 'transactions.import', 'transactions.rules', 'transactions.reconcile', 'scheduled',
  'budget', 'budget.setup', 'goals', 'goals.new',
  'plan.forecast', 'plan.calculators', 'plan.debt', 'plan.rewards',
  'invest.holdings', 'invest.trades', 'invest.trade.new', 'invest.whatif', 'invest.events', 'invest.income',
  'invest.performance', 'invest.watchlist', 'invest.securities', 'invest.inbox', 'invest.smsf',
  'reports', 'reports.networth', 'reports.cgt', 'reports.income', 'reports.gst', 'reports.vat', 'reports.budget',
  'business.invoices', 'business.contacts',
  'settings.entity', 'settings.smsf', 'settings.profile', 'settings.security', 'settings.data', 'settings.moving',
  'settings.notifications', 'settings.members',
  'help.shortcuts', 'journey',
] as const;
export type HelpKey = (typeof HELP_KEYS)[number];

export const isHelpKey = (k: string): k is HelpKey => (HELP_KEYS as readonly string[]).includes(k);

const files = import.meta.glob<string>('./*.md', { query: '?raw', import: 'default' });
const missing = (key: string) => () => Promise.resolve(`---\ntitle: Help\nkey: ${key}\n---\n\nHelp for this screen is on its way.`);

export const helpArticles = Object.fromEntries(
  HELP_KEYS.map((key) => [key, files[`./${key}.md`] ?? missing(key)]),
) as Record<HelpKey, () => Promise<string>>;

export interface HelpArticle {
  readonly title: string;
  readonly key: string;
  readonly docsUrl: string | null;
  readonly body: string;
}

/** Splits the simple `key: value` frontmatter from the markdown body. */
export function parseHelpArticle(raw: string): HelpArticle {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(raw);
  const meta: Record<string, string> = {};
  for (const line of (m?.[1] ?? '').split(/\r?\n/)) {
    const i = line.indexOf(':');
    if (i > 0) meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return { title: meta.title ?? '', key: meta.key ?? '', docsUrl: meta.docsUrl ?? null, body: m ? raw.slice(m[0].length) : raw };
}

/** react-markdown `urlTransform`: keeps help:/docs: links, resolves docs: against the docs site, drops unsafe schemes. */
export function helpUrlTransform(url: string, docsBase = '/docs/'): string {
  if (url.startsWith('help:')) return url;
  if (url.startsWith('docs:')) return docsBase + url.slice('docs:'.length);
  return /^(https?:|mailto:|#|\/)/i.test(url) ? url : '';
}

/** Titles and a few keywords for every article (the command palette searches them). Loads all articles, so call lazily. */
export const loadHelpIndex = async (): Promise<{ key: HelpKey; title: string; keywords: string[] }[]> =>
  Promise.all(HELP_KEYS.map(async (key) => {
    const a = parseHelpArticle(await helpArticles[key]());
    const words = a.body.replace(/[#*_`>[\]()]/g, ' ').split(/\s+/).filter((w) => w.length > 3).slice(0, 60);
    return { key, title: a.title || key, keywords: [key, ...words] };
  }));
